import React, { useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { CircularProgress } from '@mui/material';
import { DesignMap } from '../map';
import { type ViewMode } from './ViewModeToggle';
import { Coordinate } from '@shared/models/geography.model';
import { requestPhotorealisticRender } from '../api/design-ai.api';
import {
  MapRenderRequest,
  MapRenderResponse,
} from '@shared/models/design/map-render.model';
import { IsolatedErrorBoundary } from '@client/ErrorBoundary';
import { getRenderStoreyCount } from '@shared/helpers/design/building-design-render-prompt.helpers';
import { useDebouncedSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { logImageDataUrlToConsole } from '@client/helpers/image-decode.helpers';
import { useUser } from '@client/hooks/use-user.hooks';
import { tryCatchAwaitable } from '@shared/helpers/utils.helpers';
import { sourceTypeNeedsScreenshot } from '@shared/helpers/design/map-render.helpers';
import { useBuildingDesign } from '../hooks/design.hooks';
import {
  logRenderPromptToConsole,
  mergeClientPrompt,
  shouldLogMapRenderDebug,
} from '../helpers/design-ai-render-prompt.helpers';
import {
  buildMapCaptureSourcePayload,
  getMapCaptureDownloadSourceLabel,
} from '../helpers/generate-image-dialog.helpers';
import { IconButton } from '@ui/buttons';
import { Tooltip } from '@ui/Tooltip';
import {
  GenerateImageDialog,
  buildGenerateImageDialogDefaults,
  type GenerateImageDialogDefaults,
  type GenerateImageDialogValue,
} from './GenerateImageDialog';

type MapCameraCaptureProps = {
  map: DesignMap | undefined;
  mode: ViewMode;
  projectId: number;
  location: Coordinate | undefined;
};

type CaptureState = 'idle' | 'loading' | 'ready';

const CAPTURE_MUI_ICON: Record<
  Exclude<CaptureState, 'loading'>,
  'GetApp' | 'PhotoCamera'
> = {
  idle: 'PhotoCamera',
  ready: 'GetApp',
};

const CAPTURE_TOOLTIP_TITLE: Record<CaptureState, string> = {
  idle: 'Create photorealistic rendering',
  loading: 'Creating photorealistic rendering',
  ready: 'Download rendered image',
};

export const MapCameraCapture: React.FC<MapCameraCaptureProps> = (props) => {
  const { classes } = useStyles();
  const errorHandler = useErrorHandler();
  return (
    <IsolatedErrorBoundary
      onError={(error) => errorHandler(error)}
      fallback={(reset) => (
        <div className={classes.root}>
          <Tooltip placement="right" title="Image generation failed. Retry.">
            <IconButton
              category="mui"
              icon="PhotoCamera"
              shape="square"
              variant="overlay"
              onClick={reset}
            />
          </Tooltip>
        </div>
      )}
    >
      <MapCameraCaptureView {...props} />
    </IsolatedErrorBoundary>
  );
};

const MapCameraCaptureView: React.FC<MapCameraCaptureProps> = ({
  map,
  mode,
  projectId,
  location,
}) => {
  const { classes } = useStyles();
  const [state, setState] = useState<CaptureState>('idle');
  const [result, setResult] = useState<MapRenderResponse | undefined>(
    undefined,
  );
  const [currentRenderParams, setCurrentRenderParams] = useState<
    GenerateImageDialogValue | undefined
  >(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaults, setDialogDefaults] = useState<
    GenerateImageDialogDefaults | undefined
  >(undefined);
  const enqueueSnackbar = useDebouncedSnackbar();
  const errorHandler = useErrorHandler();
  const user = useUser();
  const buildingDesign = useBuildingDesign();

  const handleDownload = useCallback(
    (image: string) => {
      if (!currentRenderParams) {
        console.warn('No render parameters available for filename generation');
        return;
      }

      const { provider, model, sourceType } = currentRenderParams;
      const filename = `nodon-${provider}:${model}-from:${getMapCaptureDownloadSourceLabel(sourceType)}.png`;

      const anchor = document.createElement('a');
      anchor.href = image;
      anchor.download = filename;
      anchor.click();
      anchor.remove();
      setState('idle');
      setResult(undefined);
      setCurrentRenderParams(undefined);
    },
    [setState, currentRenderParams],
  );

  const handleClick = useCallback(async () => {
    if (state === 'loading') return;
    if (state === 'ready' && result?.image) {
      handleDownload(result.image);
      return;
    }
    if (!map || !map.isLoaded) {
      errorHandler(new Error('The map image is not ready yet.'));
      return;
    }
    const mapbox = map.map;
    if (!mapbox) {
      errorHandler(new Error('The Mapbox instance could not be found.'));
      return;
    }
    const cameraState: MapRenderRequest['camera'] = {
      zoom: Number(mapbox.getZoom()),
      pitch: Number(mapbox.getPitch()),
      bearing: Number(mapbox.getBearing()),
    };

    setDialogDefaults(
      buildGenerateImageDialogDefaults({
        camera: cameraState,
        mode,
        location,
        buildingDesign,
      }),
    );
    setDialogOpen(true);
  }, [
    state,
    result,
    map,
    mode,
    location,
    buildingDesign,
    errorHandler,
    handleDownload,
  ]);

  const handleDialogClose = useCallback(
    async (value: GenerateImageDialogValue | undefined) => {
      setDialogOpen(false);
      if (!value || !dialogDefaults) return;
      if (!map || !map.isLoaded) {
        errorHandler(new Error('The map image is not ready yet.'));
        return;
      }

      setState('loading');
      const generated = await tryCatchAwaitable(
        (async () => {
          const screenshot = sourceTypeNeedsScreenshot(value.sourceType)
            ? await map.captureScreenshot()
            : undefined;
          const inputPayload = buildMapCaptureSourcePayload({
            sourceType: value.sourceType,
            screenshot,
            buildingDesign,
          });

          const mergedPrompt = mergeClientPrompt({
            systemPrompt: value.systemPrompt,
            userPrompt: value.userPrompt,
            buildingDesign,
          });
          if (shouldLogMapRenderDebug(user)) {
            if (screenshot) {
              logImageDataUrlToConsole('Map screenshot sent to AI', screenshot);
            }
            logRenderPromptToConsole(mergedPrompt);
          }

          return requestPhotorealisticRender({
            ...inputPayload,
            projectId,
            mode,
            camera: dialogDefaults.camera,
            location,
            storeyCount: getRenderStoreyCount(buildingDesign),
            prompt: mergedPrompt,
            ai: {
              provider: value.provider,
              model: value.model,
            },
          });
        })(),
      );

      if (!generated.ok) {
        setState('idle');
        setCurrentRenderParams(undefined);
        try {
          errorHandler(generated.error);
        } catch {
          console.error('Design rendering error:', generated.error);
        }
        return;
      }

      enqueueSnackbar('Photorealistic rendering ready', { variant: 'success' });
      setCurrentRenderParams(value);
      setResult(generated.value);
      setState('ready');
    },
    [
      dialogDefaults,
      map,
      buildingDesign,
      projectId,
      mode,
      location,
      enqueueSnackbar,
      errorHandler,
      user,
    ],
  );

  return (
    <div className={classes.root}>
      <Tooltip
        placement="right"
        title={CAPTURE_TOOLTIP_TITLE[state]}
        showWhenDisabled
      >
        {state === 'loading' ? (
          <div className={classes.loadingTile}>
            <CircularProgress color="inherit" size={18} />
          </div>
        ) : (
          <IconButton
            category="mui"
            disabled={!map}
            icon={CAPTURE_MUI_ICON[state]}
            shape="square"
            variant="overlay"
            onClick={handleClick}
          />
        )}
      </Tooltip>
      {dialogOpen && dialogDefaults && (
        <GenerateImageDialog
          open={dialogOpen}
          defaults={dialogDefaults}
          onClose={handleDialogClose}
          mode={mode}
          location={location}
          buildingDesign={buildingDesign}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    zIndex: 3,
  },
  loadingTile: {
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[900],
    boxShadow: theme.shadows[4],
  },
}));
